/**
 * Shell Application
 *
 * Pure router that composes route configurations from MFEs.
 * ZERO logic - just composition.
 *
 * Provider Hierarchy:
 * 1. AppRoot (theme + language + error boundary + toaster)
 * 2. Suspense (lazy-loaded MFE fallback)
 * 3. RouterProvider (routing with data loading)
 * 4. MFEs (self-contained with MFE-specific context and guards)
 *
 * Routing Strategy:
 * - Shell composes route tree via createBrowserRouter
 * - All guards, loaders, and logic handled by MFEs
 */

import { Suspense } from 'react'
import { RouterProvider, createBrowserRouter } from 'react-router-dom'
import { AppRoot } from '@innolab-trader/mfe-shared'
import { Spinner } from '@innolab-trader/ui'
import { getAuthRoutes } from '@innolab-trader/mfe-auth/routes'
import { getWorkspaceRoutes } from '@innolab-trader/mfe-workspace/routes'
import { getMarketplaceRoutes } from '@innolab-trader/mfe-marketplace/routes'
import { getManagementRoutes } from '@innolab-trader/mfe-management/routes'
import { getReportsRoutes } from '@innolab-trader/mfe-reports/routes'

/**
 * Loading fallback for lazy-loaded routes
 */
function LoadingFallback() {
    return (
        <div className="flex h-screen items-center justify-center bg-background">
            <div className="text-center">
                <Spinner size="xl" />
                <p className="mt-4 text-sm text-muted-foreground">Loading...</p>
            </div>
        </div>
    )
}

/**
 * Compose router from MFE route configurations
 */
const router = createBrowserRouter([
    ...getWorkspaceRoutes(),
    ...getMarketplaceRoutes(),
    ...getManagementRoutes(),
    ...getReportsRoutes(),
    ...getAuthRoutes(),
])

function App() {
    return (
        <AppRoot>
            <Suspense fallback={<LoadingFallback />}>
                <RouterProvider router={router} />
            </Suspense>
        </AppRoot>
    )
}

export default App
